import { PageSettings, THEME_PRESETS } from '@/types/editor';

/** Returns the CSS animation class for the active preset, or null if none. */
export function getPresetAnimationClass(settings: PageSettings): string | null {
  if (settings.background_mode !== 'preset' || !settings.theme) return null;
  const preset = THEME_PRESETS.find(
    (p) => p.name.toLowerCase() === settings.theme.toLowerCase()
  );
  if (!preset?.animationClass) return null;

  return preset.animationClass;
}

function hexToRgba(hexColor: string, alpha: number): string {
  const sanitized = hexColor.replace('#', '');
  const normalized = sanitized.length === 3
    ? sanitized.split('').map((char) => `${char}${char}`).join('')
    : sanitized;

  if (normalized.length !== 6) {
    return `rgba(0,0,0,${alpha})`;
  }

  const red = Number.parseInt(normalized.slice(0, 2), 16);
  const green = Number.parseInt(normalized.slice(2, 4), 16);
  const blue = Number.parseInt(normalized.slice(4, 6), 16);

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

/** Returns the CSS background value for the overlay div when a video background is active. */
export function getVideoOverlayStyle(settings: PageSettings): string | null {
  if (!settings.background_overlay_enabled) return null;
  const alpha = Math.min(100, Math.max(0, settings.background_overlay_opacity)) / 100;
  if (settings.background_type === 'gradient') {
    const start = hexToRgba(settings.background_gradient_start || settings.background_color, alpha);
    const end = hexToRgba(settings.background_gradient_end || settings.background_color, alpha);
    return `linear-gradient(135deg, ${start}, ${end})`;
  }
  const color = hexToRgba(settings.background_color, alpha);
  return `linear-gradient(${color}, ${color})`;
}

export function getBackgroundStyle(settings: PageSettings): string {
  const backgroundMode = settings.background_mode || 'preset';

  if (backgroundMode === 'custom' && settings.background_image_url) {
    if (settings.background_overlay_enabled) {
      const overlayAlpha = Math.min(100, Math.max(0, settings.background_overlay_opacity)) / 100;

      if (settings.background_type === 'gradient') {
        const startHex = settings.background_gradient_start || settings.background_color;
        const endHex = settings.background_gradient_end || settings.background_color;
        const startColor = hexToRgba(startHex, overlayAlpha);
        const endColor = hexToRgba(endHex, overlayAlpha);

        return `linear-gradient(135deg, ${startColor}, ${endColor}), url(${settings.background_image_url}) center / cover no-repeat`;
      }

      const overlayColor = hexToRgba(settings.background_color, overlayAlpha);
      return `linear-gradient(${overlayColor}, ${overlayColor}), url(${settings.background_image_url}) center / cover no-repeat`;
    }

    return `url(${settings.background_image_url}) center / cover no-repeat`;
  }

  if (settings.background_type === 'gradient'
      && settings.background_gradient_start
      && settings.background_gradient_end) {
    return `linear-gradient(135deg, ${settings.background_gradient_start}, ${settings.background_gradient_end})`;
  }

  return settings.background_color;
}
